/**
 * Members-only tenant apps (tenant config `access.requireSignIn`).
 *
 * The club's events page is the whole public face of the app: anyone may read
 * what is on, and the app root lands there. Every other path — registering,
 * standings, the arena, a player's own pages — needs a signed-in member, so a
 * signed-out visitor is sent to the player login with `next` pointing back at
 * where they were headed.
 *
 * `scopePrefix` keeps this off everything outside the app. A club with its own
 * site has it at `/` with the app under `/events`, and those pages stay public.
 *
 * This gates the app, not the data: the public read APIs behind these pages
 * stay open, so treat it as navigation rather than a security boundary.
 *
 * Kept free of React and context imports so the rule is testable on its own —
 * `TenantAccessGate` is the thin wiring around it.
 */

/** Paths a signed-out visitor always reaches, or they could never sign in. */
export const ALWAYS_PUBLIC_PATHS = [
  '/player/login',
  '/player/auth/callback',
  '/auth/callback',
  '/sign-in',
  '/sign-up',
  // The staff door. It gates itself — signed out it is the ChessDirector
  // sign-in — and sending a director to the members' player login instead
  // would leave them nowhere to sign in as themselves.
  '/manage',
  '/org/join',
  '/club/join',
  '/terms',
  '/privacy',
  '/cookies',
  '/network-error',
];

export const PLAYER_LOGIN_PATH = '/player/login';

function normalize(path: string): string {
  if (!path) return '/';
  const withSlash = path.startsWith('/') ? path : `/${path}`;
  const trimmed = withSlash.length > 1 ? withSlash.replace(/\/+$/, '') : withSlash;
  return trimmed || '/';
}

/** True when `pathname` is `allowed` itself or something nested under it. */
function matchesPath(pathname: string, allowed: string): boolean {
  const target = normalize(allowed);
  return pathname === target || pathname.startsWith(`${target}/`);
}

/**
 * Is this tenant path readable while signed out? Used both by the gate and by
 * the site nav, so a members-only site offers a signed-out visitor only the
 * links that will actually open.
 */
export function isPublicTenantPath(pathname: string, publicPaths?: string[] | null): boolean {
  const allowed = [...(publicPaths || []).map(normalize), ...ALWAYS_PUBLIC_PATHS];
  const target = normalize(pathname);
  return allowed.some((candidate) => matchesPath(target, candidate));
}

/**
 * Does the members-only rule reach this URL? `scopePrefix` is the app's mount
 * point on a host whose root is the club's own site; null means the whole app.
 * Takes the real pathname, prefix and all.
 */
export function isWithinAccessScope(pathname: string, scopePrefix?: string | null): boolean {
  if (!scopePrefix) return true;
  const prefix = normalize(scopePrefix);
  return matchesPath(normalize(pathname), prefix);
}

export type AccessDecision =
  | { type: 'allow' }
  | { type: 'wait' }
  | { type: 'redirect'; to: string };

export interface AccessInput {
  /** Tenant path with any app prefix (/events, /org/:slug) already stripped. */
  pathname: string;
  requireSignIn: boolean;
  /**
   * False when the current URL sits outside the app the rule covers — a club's
   * own site at `/`, with the app under `/events`. Defaults to in scope.
   */
  inScope?: boolean;
  /** Where the app root lands, prefix already stripped. */
  homePath?: string | null;
  /** Readable while signed out, prefixes already stripped. */
  publicPaths?: string[] | null;
  tenantLoading: boolean;
  signedIn: boolean;
  authLoading: boolean;
}

/**
 * The gate's whole decision. `wait` means "render nothing yet" — a stored
 * session is still being restored and either answer would be a guess.
 */
export function decideTenantAccess(input: AccessInput): AccessDecision {
  const { requireSignIn, tenantLoading, signedIn, authLoading, inScope = true } = input;

  // Until the tenant config lands we do not know whether this app is gated.
  // Rendering on is the right call for every other site — a blank first paint
  // everywhere is a worse trade than a moment of the events page here.
  if (!requireSignIn || !inScope || tenantLoading) return { type: 'allow' };

  const pathname = normalize(input.pathname);
  const homePath = input.homePath ? normalize(input.homePath) : null;

  // The events page is the app's home page.
  if (pathname === '/' && homePath && homePath !== '/') {
    return { type: 'redirect', to: homePath };
  }

  if (signedIn) return { type: 'allow' };
  if (isPublicTenantPath(pathname, input.publicPaths)) return { type: 'allow' };

  // A stored session is still being checked — redirecting now would bounce a
  // signed-in member to the login page.
  if (authLoading) return { type: 'wait' };

  return { type: 'redirect', to: PLAYER_LOGIN_PATH };
}
